import React, { useState, useRef, useEffect } from "react";
import RangeSlider from "../components/modernized/RangeSlider";
import themePropTypes from "../theme/theme-prop-types";

const AudioField = ({ field }) => {
  // state
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const conf = field.data;
  const {
    url,
    accessibilityDescription,
    accessibilityAudioPlayPause,
    accessibilityAudioBackward,
    accessibilityAudioForward
  } = conf;

  const audioPlayer = useRef(); // reference audio component

  useEffect(() => {
    const seconds = Math.floor(audioPlayer.current.duration);
    setDuration(seconds);
  }, [audioPlayer?.current?.loadedmetadata, audioPlayer?.current?.readyState]);

  const calculateTime = secs => {
    const minutes = Math.floor(secs / 60);
    const returnedMinutes = minutes < 10 ? `${minutes}` : `${minutes}`;
    const seconds = Math.floor(secs % 60);
    const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
    return `${returnedMinutes}:${returnedSeconds}`;
  };

  const togglePlayPause = () => {
    const prevValue = isPlaying;
    setIsPlaying(!prevValue);
    if (!prevValue) {
      audioPlayer.current.play();
    } else {
      audioPlayer.current.pause();
    }
  };

  const changeRange = e => {
    audioPlayer.current.currentTime = e;
    setCurrentTime(e);
  };

  const backwardControl = () => {
    if (Number(audioPlayer.current.currentTime) + 15 < 0) {
      audioPlayer.current.currentTime = 0;
    } else {
      audioPlayer.current.currentTime =
        Number(audioPlayer.current.currentTime) - 15;
    }
  };

  const forwardControl = () => {
    if (Number(audioPlayer.current.currentTime) + 15 > duration) {
      audioPlayer.current.currentTime = duration;
    } else {
      audioPlayer.current.currentTime =
        Number(audioPlayer.current.currentTime) + 15;
    }
  };

  const handleTimeUpdate = () => {
    setCurrentTime(audioPlayer.current.currentTime);
  };

  const getRemainingTime = (newDuration, newCurrentTime) => {
    if (Number.isNaN(newDuration) || newCurrentTime > newDuration) {
      return `-0:00`;
    } else {
      const finalDuration = calculateTime(newDuration - newCurrentTime);
      return `-${finalDuration}`;
    }
  };

  return (
    <div className='audioPlayer'>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        className='audioWidget'
        ref={audioPlayer}
        preload='metadata'
        src={url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={event => setDuration(event.target.duration)}
        aria-label={accessibilityDescription}
        onEnded={togglePlayPause}
      />
      <RangeSlider
        min={0}
        max={duration}
        step={1}
        showCurrentValueLabel={false}
        onChange={changeRange}
        showMinMaxLabels
        i18nHandleLabels='slider'
        i18nMinLabel={calculateTime(currentTime)}
        i18nMaxLabel={getRemainingTime(duration, currentTime)}
        value={currentTime}
      />
      <div className='audioControls'>
        <button
          className='backward'
          aria-label={accessibilityAudioBackward}
          type='button'
          onClick={backwardControl}
        />
        <button
          type='button'
          className={`${isPlaying ? "pause" : "play"}`}
          aria-label={accessibilityAudioPlayPause}
          onClick={togglePlayPause}
        />
        <button
          className='forward'
          aria-label={accessibilityAudioForward}
          type='button'
          onClick={forwardControl}
        />
      </div>
    </div>
  );
};

AudioField.propTypes = themePropTypes.audio;

export default AudioField;
